import {
  CardContent,
  CardHeader,
  CardLarge,
  CardSeparator,
  Form,
  FormGroup,
  Heading1,
  Size,
  ValidationErrorMessage,
  useTestId,
  FormLabel,
  CheckBox
} from '@cmctechnology/phoenix-stockbroking-web-design';
import styled from 'styled-components';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { GoBackIcon } from '../../Components/GoBackIcon';
import { ProfileCardFooter } from '../../Components/ProfileCardFooter';
import { INextPage, IPreviousPage } from '../../models/IPageNavigation';
import { ISgpFinancialBackground } from '../../models/sgp/profile';
import { saveFinancialBackground } from '../../Store/Actions';
import { IStore } from '../../Store/Store';
import { INCOME_SOURCE, WEALTH_SOURCE } from '../../constants/sgp/additionalQuestions';
import { createKeyValueTranslatedToOptionList } from '../../common/mappings';
import { useAdditionalQuestionsValidators } from '../../hooks/sgp/validators/useAdditionalQuestionsValidators';

const FormLabelTextLeft = styled(FormLabel)`
  text-align: left;
`;

export interface IAdditionalQuestionsProps extends INextPage, Partial<IPreviousPage> {}

export const AdditionalQuestions: React.FC<IAdditionalQuestionsProps> = ({ onNext, onPrevious, ...rest }) => {
  const { t } = useTranslation();
  const { generateTestId } = useTestId(rest, `sg.additionalQuestions`);
  const dispatch = useDispatch();

  const financialBackground = useSelector((store: IStore) => store.remote.sgp.profile.financialBackground);

  const hasIncomeRef = useRef<HTMLInputElement | null>(null);
  const incomeSourceRef = useRef<HTMLInputElement | null>(null);
  const wealthSourceRef = useRef<HTMLInputElement | null>(null);

  const { incomeSourceValidator, wealthSourceValidator, hasIncomeSourceValidator, onMultiValueChange } = useAdditionalQuestionsValidators(financialBackground);

  const incomeSourceOptions = createKeyValueTranslatedToOptionList(INCOME_SOURCE, t);
  const wealthSourceOptions = createKeyValueTranslatedToOptionList(WEALTH_SOURCE, t);

  const onNextClicked = () => {
    const updatedFinancialBackground: ISgpFinancialBackground = {
      ...financialBackground,
      hasIncomeSource: hasIncomeSourceValidator.value,
      incomeSource: hasIncomeSourceValidator.value ? incomeSourceValidator.value?.map((x) => x?.value) : undefined,
      wealthSource: wealthSourceValidator.value?.map((x) => x?.value)
    };

    dispatch(saveFinancialBackground(updatedFinancialBackground));

    onNext();
  };

  return (
    <Form>
      <CardLarge>
        <CardHeader>
          {onPrevious && <GoBackIcon onClick={onPrevious} />}
          <Heading1>{t('Additional Questions')}</Heading1>
        </CardHeader>
        <CardSeparator />
        <CardContent>
          <FormGroup ref={hasIncomeRef}>
            <FormLabel>{t('Do you have any other source of income?')}</FormLabel>
            <FormGroup>
              <CheckBox
                label={t('Yes')}
                checked={hasIncomeSourceValidator.value === true}
                onChange={(e) => hasIncomeSourceValidator.handleEvent(e.target.checked)}
                invalid={hasIncomeSourceValidator.invalid}
                {...generateTestId(`yes`)}
              />
            </FormGroup>
            <FormGroup>
              <CheckBox
                label={t('No')}
                checked={hasIncomeSourceValidator.value === false}
                onChange={(e) => hasIncomeSourceValidator.handleEvent(!e.target.checked)}
                invalid={hasIncomeSourceValidator.invalid}
                {...generateTestId(`no`)}
              />
            </FormGroup>
            <ValidationErrorMessage size={Size.Medium} validator={hasIncomeSourceValidator} />
          </FormGroup>
          {hasIncomeSourceValidator.value && (
            <FormGroup ref={incomeSourceRef} {...generateTestId(`income-source`)}>
              <FormLabelTextLeft>{t('What are the other source(s) of income? You may select more than 1 of the below options.')}</FormLabelTextLeft>
              {incomeSourceOptions.map((option) => (
                <FormGroup key={option.value}>
                  <CheckBox
                    label={option.label}
                    checked={incomeSourceValidator?.value?.map((x) => x?.value).includes(option.value) ?? false}
                    onChange={() => onMultiValueChange(incomeSourceValidator, option)}
                    invalid={incomeSourceValidator.invalid}
                    {...generateTestId(`incomeSource.checkbox`)}
                  />
                </FormGroup>
              ))}
              <ValidationErrorMessage size={Size.Medium} validator={incomeSourceValidator} />
            </FormGroup>
          )}
          <FormGroup ref={wealthSourceRef}>
            <FormLabelTextLeft>{t('What is your source(s) of wealth? You may select more than 1 of the below options.')}</FormLabelTextLeft>
            {wealthSourceOptions.map((option) => (
              <FormGroup key={option.value}>
                <CheckBox
                  label={option.label}
                  checked={wealthSourceValidator?.value?.map((x) => x?.value).includes(option.value) ?? false}
                  onChange={() => onMultiValueChange(wealthSourceValidator, option)}
                  invalid={wealthSourceValidator.invalid}
                  {...generateTestId(`wealthSource.checkbox`)}
                />
              </FormGroup>
            ))}
            <ValidationErrorMessage size={Size.Medium} validator={wealthSourceValidator} />
          </FormGroup>
        </CardContent>
        <ProfileCardFooter
          nextButtonLabel={t('Confirm')}
          saveButtonLabel={t('Confirm')}
          onNext={onNextClicked}
          onCancel={onNext}
          validators={[incomeSourceValidator, wealthSourceValidator, hasIncomeSourceValidator]}
          {...generateTestId(`confirm`)}
        />
      </CardLarge>
    </Form>
  );
};
